import React, { useState, useEffect, useRef, useCallback } from 'react';
import config from '../../config/config';

const API_BASE = config.api.baseUrl;

const DEBOUNCE_MS = 300;
const MIN_SEARCH_CHARS = 2;
const CACHE_TTL_MS = 5000;
const RESULT_LIMIT = 10;

// Module-level cache: { 'object:query' -> { ts, results } }
// Shared across LookupField instances so re-mounting an edit form does not
// re-issue the same /records?search= request the user just typed.
const _resultCache = new Map();

function cacheGet(object, q) {
  const k = `${object}:${q}`;
  const hit = _resultCache.get(k);
  if (!hit) return null;
  if (Date.now() - hit.ts > CACHE_TTL_MS) {
    _resultCache.delete(k);
    return null;
  }
  return hit.results;
}

function cachePut(object, q, results) {
  _resultCache.set(`${object}:${q}`, { ts: Date.now(), results });
}

function pickDisplayName(rec) {
  return (
    rec.Name ||
    [rec.FirstName, rec.LastName].filter(Boolean).join(' ').trim() ||
    rec.Subject ||
    rec.AppointmentNumber ||
    rec.WorkOrderNumber ||
    rec.CaseNumber ||
    rec.Id
  );
}

/**
 * Generic Salesforce-style lookup field. Renders a typeahead input that
 * searches the configured target object via /api/objects/{object}/records.
 *
 * Optimistic display: shows `displayValue` immediately on mount (typically
 * the resolved `<Field>__Name` the backend already returned with the record),
 * so the user sees a human-readable name before they ever click into the
 * field. Typing replaces the displayed text with the live search query.
 *
 * Props
 *   object         api name of the target object (e.g. "Account")
 *   value          current FK id (or '' / null)
 *   displayValue   resolved name to show optimistically
 *   onChange       (id, name) — called when the user picks a record or clears it
 *   placeholder    optional
 *   disabled       optional
 */
export default function LookupField({
  object,
  value,
  displayValue,
  onChange,
  placeholder,
  disabled = false,
}) {
  // The text displayed inside the input. We track this separately from
  // `value`/`displayValue` because the user can type freely without
  // committing — only a click on a dropdown row mutates the parent state.
  const [text, setText] = useState(displayValue || '');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const inflightRef = useRef(null); // AbortController for the active fetch

  // Sync external displayValue → local text when the parent record reloads
  // or another field's change triggered a re-resolve.
  useEffect(() => {
    setText(displayValue || '');
  }, [displayValue]);

  // Click outside closes the dropdown without changing the committed value.
  useEffect(() => {
    function onDocMouseDown(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setIsOpen(false);
        // Restore the committed name if the user typed but didn't pick.
        setText(displayValue || '');
      }
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [displayValue]);

  const runSearch = useCallback(
    async (q) => {
      if (!object) return;
      const trimmed = q.trim();
      if (trimmed.length < MIN_SEARCH_CHARS) {
        setResults([]);
        return;
      }

      const cached = cacheGet(object, trimmed);
      if (cached) {
        setResults(cached);
        return;
      }

      if (inflightRef.current) inflightRef.current.abort();
      const ctrl = new AbortController();
      inflightRef.current = ctrl;
      setLoading(true);
      try {
        const url = `${API_BASE}/api/objects/${encodeURIComponent(object)}/records?search=${encodeURIComponent(trimmed)}&limit=${RESULT_LIMIT}`;
        const res = await fetch(url, { signal: ctrl.signal });
        if (!res.ok) {
          setResults([]);
          return;
        }
        const data = await res.json();
        const recs = data.records || [];
        cachePut(object, trimmed, recs);
        setResults(recs);
      } catch (err) {
        if (err.name !== 'AbortError') {
          // eslint-disable-next-line no-console
          console.warn(`[LookupField] search failed for ${object}:`, err);
          setResults([]);
        }
      } finally {
        if (inflightRef.current === ctrl) inflightRef.current = null;
        setLoading(false);
      }
    },
    [object]
  );

  // Debounced search whenever `text` changes via user typing.
  useEffect(() => {
    if (!isOpen) return undefined;
    const t = setTimeout(() => runSearch(text), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [text, isOpen, runSearch]);

  function handleTextChange(e) {
    setText(e.target.value);
    setIsOpen(true);
  }

  function handleFocus() {
    setIsOpen(true);
  }

  function handleSelect(rec) {
    const name = pickDisplayName(rec);
    setText(name);
    setIsOpen(false);
    setResults([]);
    if (onChange) onChange(rec.Id, name);
  }

  function handleClear(e) {
    e.preventDefault();
    e.stopPropagation();
    setText('');
    setResults([]);
    setIsOpen(false);
    if (onChange) onChange('', '');
  }

  function handleKeyDown(e) {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setText(displayValue || '');
    }
  }

  const showClear = !disabled && (value || text);

  return (
    <div className="lookup-field search-field" ref={wrapRef} style={{ position: 'relative' }}>
      <input
        type="text"
        className="slds-input"
        value={text}
        onChange={handleTextChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder || (object ? `חפש ${object}...` : 'חפש...')}
        disabled={disabled}
        autoComplete="off"
        style={{ paddingInlineEnd: showClear ? '28px' : undefined }}
      />
      {loading && (
        <span
          className="search-loading"
          style={{ position: 'absolute', insetInlineEnd: '32px', top: '50%', transform: 'translateY(-50%)' }}
        >
          ⏳
        </span>
      )}
      {showClear && (
        <button
          type="button"
          onClick={handleClear}
          aria-label="נקה"
          style={{
            position: 'absolute',
            insetInlineEnd: '8px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'transparent',
            border: 'none',
            color: '#54698d',
            cursor: 'pointer',
            fontSize: '14px',
            lineHeight: 1,
            padding: 0,
          }}
        >
          ✕
        </button>
      )}
      {isOpen && (results.length > 0 || (text.trim().length >= MIN_SEARCH_CHARS && !loading)) && (
        <div
          className="search-dropdown"
          style={{
            position: 'absolute',
            zIndex: 1000,
            top: '100%',
            insetInlineStart: 0,
            insetInlineEnd: 0,
            background: '#fff',
            border: '1px solid #d8dde6',
            borderRadius: '4px',
            marginTop: '2px',
            maxHeight: '240px',
            overflowY: 'auto',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
          }}
        >
          {results.length === 0 ? (
            <div style={{ padding: '8px 12px', color: '#54698d', fontSize: '13px' }}>
              לא נמצאו תוצאות
            </div>
          ) : (
            results.map((rec) => (
              <div
                key={rec.Id}
                className="dropdown-item"
                onClick={() => handleSelect(rec)}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #f3f3f3',
                }}
                onMouseDown={(e) => e.preventDefault()}
              >
                <div style={{ fontSize: '13px', color: '#16325c' }}>{pickDisplayName(rec)}</div>
                <div style={{ fontSize: '11px', color: '#706e6b' }}>{rec.Id}</div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
